import { IconButton, Modal } from "@fluentui/react";
import { CloudFramePrimaryButton } from "@cloudframe/button";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import { useState } from "react";
import Authorization from "../auth/authorization";
import { UserPermission } from "../../constants/permissions.constants";
import { useDetailModalStyles } from "./continuum-scanner-license-detail-modal.styles";
import { formatLicenseTypeLabel } from "./continuum-scanner-license-list.utils";

interface IContinuumScannerLicenseDetailModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  license: any;
  onApprove?: () => Promise<void>;
  onDecline?: (reason: string) => Promise<void>;
}

const STATUS_PILL_CLASS_BY_STATUS: Record<string, string> = {
  ACTIVE: "statusActive",
  PENDING: "statusPending",
  ACCEPTED: "statusAccepted",
  DECLINED: "statusDeclined",
  DEACTIVATION_REQUESTED: "statusDeactivationRequested",
  DEACTIVATED: "statusDeactivated",
  EXPIRED: "statusExpired",
};

const formatDate = (value: any): string => {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ContinuumScannerLicenseDetailModal = (
  props: IContinuumScannerLicenseDetailModalProps
) => {
  const { isOpen, onDismiss, license, onApprove, onDecline } = props;
  const classes = useDetailModalStyles();
  const [denialReason, setDenialReason] = useState("");
  const [denialError, setDenialError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!license) return null;

  const isContinuum = license.product !== "Scanner";
  const canApproveOrDecline = isContinuum && license.licenseStatus === "PENDING";

  const handleApprove = async () => {
    if (!onApprove) return;
    setIsSubmitting(true);
    try {
      await onApprove();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!onDecline) return;
    if (!denialReason.trim()) {
      setDenialError("Please provide a reason for declining the license");
      return;
    }
    setIsSubmitting(true);
    try {
      await onDecline(denialReason);
      setDenialReason("");
      setDenialError("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const pillClass =
    (classes as any)[STATUS_PILL_CLASS_BY_STATUS[license.licenseStatus]] || "";

  const rows: Array<{ label: string; value: any }> = [
    { label: "Customer Name", value: license.customerName },
    { label: "Email", value: license.email },
    { label: "Department", value: license.department },
    { label: "License Type", value: formatLicenseTypeLabel(license.licenseType) },
    ...(isContinuum
      ? [{ label: "License Category", value: license.licenseCategory }]
      : []),
    {
      label: "Effective Date",
      value: formatDate(
        isContinuum ? license.platformEffectiveDate : license.scannerEffectiveDate
      ),
    },
    {
      label: "Expiry Date",
      value: formatDate(
        isContinuum ? license.platformExpiryDate : license.scannerExpiryDate
      ),
    },
    ...(isContinuum
      ? [
          {
            label: "Modernization Allowed",
            value: license.modernizationAllowed ? "Yes" : "No",
          },
          { label: "Lines of Code", value: license.linesOfCode },
          {
            label: "Carryover LOC Enabled",
            value: license.carryOverLOC ? "Yes" : "No",
          },
          { label: "Continuum Carryover", value: license.continuumCarryover },
        ]
      : []),
    { label: "Jira Ticket", value: license.jiraTicket },
    { label: "Service Desk ID", value: license.serviceDeskID },
    ...(isContinuum && license.licenseStatus === "DECLINED"
      ? [
          { label: "Decline Reason", value: license.denialReason },
          { label: "Declined By", value: license.approvedBy },
        ]
      : []),
    ...(isContinuum &&
    ["ACCEPTED", "ACTIVE", "DEACTIVATION_REQUESTED", "DEACTIVATED"].includes(
      license.licenseStatus
    )
      ? [{ label: "Approved By", value: license.approvedBy }]
      : []),
  ];

  return (
    <Modal
      titleAriaId="continuumScannerLicenseDetailTitle"
      isOpen={isOpen}
      onDismiss={onDismiss}
      isModeless={false}
      isBlocking={true}
      dragOptions={undefined}
      styles={{
        main: {
          borderRadius: 20,
          maxWidth: "95vw",
          maxHeight: "90vh",
          display: "flex",
        },
        scrollableContent: {
          overflowY: "auto",
          overflowX: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <div
        style={{
          width: "min(35rem, 92vw)",
          maxWidth: "100%",
          boxSizing: "border-box",
          padding: "2rem",
        }}
      >
        <div className={classes.header}>
          <div className={classes.headerText}>
            <h3 className={classes.title}>License details</h3>
            <p className={classes.subtitle}>
              {license.product} &middot; {license.licenseId}
            </p>
          </div>
          <div className={classes.headerRight}>
            <span className={`${classes.statusPill} ${pillClass}`}>
              {license.licenseStatus}
            </span>
            <IconButton
              iconProps={{ iconName: "Cancel", style: { fontSize: "1.1rem" } }}
              ariaLabel="Close popup modal"
              onClick={onDismiss}
            />
          </div>
        </div>

        <div className={classes.detailCard}>
          {rows.map((row) => (
            <div className={classes.detailRow} key={row.label}>
              <p className={classes.detailLabel}>{row.label}</p>
              <p className={classes.detailValue}>{row.value ?? "N/A"}</p>
            </div>
          ))}
        </div>

        {canApproveOrDecline && (
          <div className={classes.approvalSection}>
            <h3 className={classes.approvalTitle}>License approval</h3>
            <CloudFrameTextBox
              placeholder="Decline reason (required to decline)"
              value={denialReason}
              errorMessage={denialError}
              onChange={(_, value) => {
                setDenialReason(value || "");
                setDenialError("");
              }}
            />
            <div className={classes.buttonRow}>
              <Authorization>
                <CloudFramePrimaryButton
                  key={UserPermission.P_APPROVE_CONTINUUM_LICENSE}
                  text="Approve license"
                  onClick={handleApprove}
                  disabled={isSubmitting}
                  className={classes.approveButton}
                />
                <CloudFramePrimaryButton
                  key={UserPermission.P_DECLINE_CONTINUUM_LICENSE}
                  text="Decline license"
                  onClick={handleDecline}
                  disabled={isSubmitting}
                  className={classes.declineButton}
                />
              </Authorization>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ContinuumScannerLicenseDetailModal;
